import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  memo,
} from "react";
import PropTypes from "prop-types";
import {
  Box,
  Flex,
  Image,
  Text,
  Spinner,
  useOutsideClick,
  usePopper,
  Portal,
} from "@chakra-ui/react";
import useEventListener from "../../../_hooks/useEventListener";

const wrapperStyles = {
  zIndex: "popover",
  width: "12rem",
  maxHeight: "16rem",
  overflow: "hidden",
  overflowY: "auto",
  background: "gray.500",
  borderColor: "white",
  boxShadow: "sm",
  borderRadius: "md",
  borderWidth: "1px",
  paddingTop: "1",
  paddingBottom: "1",
};

const itemStyles = {
  alignItems: "center",
  padding: "1",
  fontSize: "sm",
  cursor: "pointer",
  outline: "none",
  ":focus": {
    backgroundColor: "whiteAlpha.100",
  },
  ":not(:first-of-type)": {
    borderTop: "1px solid",
    borderTopColor: "whiteAlpha.50",
  },
};

const AdvancedMessageMention = ({
  loading,
  items,
  node,
  onSelect,
  onExit,
  onViewFocus,
}) => {
  const itemsRefs = [];
  const ref = useRef(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const { popperRef, referenceRef } = usePopper({
    placement: "bottom-start",
    strategy: "fixed",
  });
  const active = useMemo(
    () => node && (loading || items?.length > 0),
    [node, loading, items]
  );

  const setRef = (el) => {
    ref.current = el;
    popperRef(el);
  };

  const handleSelect = useCallback(
    (index) => () => {
      onSelect(items[index]);
    },
    [items, onSelect]
  );

  const handleMouseEnter = useCallback(
    (index) => () => {
      setActiveIndex(index);
    },
    [setActiveIndex]
  );

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === "Escape" && onExit) {
        event.preventDefault();
        onExit?.();
        return false;
      }

      if (
        event.key === "Enter" &&
        active &&
        activeIndex !== null &&
        items[activeIndex]
      ) {
        event.preventDefault();
        onSelect(items[activeIndex]);
        return false;
      }

      if (event.key === "ArrowDown" && active) {
        event.preventDefault();
        setActiveIndex(Math.min(activeIndex + 1, items.length - 1));
        return false;
      }

      if (event.key === "ArrowUp" && active) {
        event.preventDefault();
        setActiveIndex(Math.max(-1, activeIndex - 1));
        return false;
      }

      return null;
    },
    [active, items, activeIndex, onSelect, onExit]
  );

  useEventListener("keydown", handleKeyDown, document, true);

  useOutsideClick({
    ref,
    handler: () => {
      onExit?.();
    },
  });

  useEffect(() => {
    referenceRef(node);
  }, [node]);

  useEffect(() => {
    if (!active) return;

    setActiveIndex(-1);
  }, [items]);

  useEffect(() => {
    if (!active) return;

    if (activeIndex > -1 && itemsRefs[activeIndex]) {
      itemsRefs[activeIndex]?.focus();
    } else {
      onViewFocus?.();
    }
  }, [activeIndex]);

  if (!active) {
    return null;
  }

  return (
    <Portal>
      <Box ref={setRef} sx={wrapperStyles}>
        {loading ? (
          <Flex alignItems="center" justifyContent="center" padding="1">
            <Spinner color="brand.primary" size="md" />
          </Flex>
        ) : (
          items.map((item, index) => (
            <Flex
              key={item.id}
              tabIndex="-1"
              ref={(el) => {
                itemsRefs[index] = el;
              }}
              onClick={handleSelect(index)}
              onMouseEnter={handleMouseEnter(index)}
              sx={itemStyles}
            >
              <Image
                src={item.image}
                fallback={<Box width="2em" height="2em" />}
                boxSize="2em"
                objectFit="cover"
              />
              <Box paddingLeft="2" maxWidth="calc(100% - 2em)">
                <Text
                  title={item.name}
                  isTruncated
                  fontSize="1em"
                  lineHeight="1.2em"
                  color="white"
                >
                  {item.name}
                </Text>
                <Text
                  title={item.text}
                  isTruncated
                  fontSize="0.75em"
                  lineHeight="1.2em"
                  color="gray.300"
                >
                  {item.text}
                </Text>
              </Box>
            </Flex>
          ))
        )}
      </Box>
    </Portal>
  );
};

AdvancedMessageMention.propTypes = {
  loading: PropTypes.bool,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    })
  ).isRequired,
  node: PropTypes.instanceOf(Node),
  onSelect: PropTypes.func.isRequired,
  onExit: PropTypes.func,
  onViewFocus: PropTypes.func,
};

AdvancedMessageMention.defaultProps = {
  loading: undefined,
  node: undefined,
  onExit: undefined,
  onViewFocus: undefined,
};

export default memo(AdvancedMessageMention);
